import { useMemo } from 'react';
import { validateFormulaStructure, createUnavailableQuestionsError, createQuestionMap, tokenizeFormula } from './formulaUtils';

import type { CampaignQuestion } from '../CampaignQuestionsEditor/types';
import type { FormulaToken } from './FormulaValidator';

interface FormulaValidationResult {
    isValid: boolean;
    referencedQuestions: Array<{ ref: string; questionIndex: number; question: CampaignQuestion }>;
    formulaTokens: FormulaToken[];
    validationError?: string;
}

/**
 * Custom hook to parse and validate a formula string.
 */
export const useFormulaValidation = (formula: string, questions: CampaignQuestion[]): FormulaValidationResult => {
    return useMemo(() => {
        const trimmedFormula = formula.trim();

        if (!trimmedFormula) {
            return {
                isValid: false,
                referencedQuestions: [],
                formulaTokens: [],
            };
        }

        const questionMap = createQuestionMap(questions);
        const { tokens, referencedQuestions, unavailableUuids } = tokenizeFormula(trimmedFormula, questionMap);

        // Handle formulas with unavailable questions
        if (unavailableUuids.length > 0) {
            const validationError = createUnavailableQuestionsError(unavailableUuids, questions);

            return {
                isValid: false,
                referencedQuestions,
                formulaTokens: tokens,
                validationError,
            };
        }

        // Validate formula structure
        const hasQuestions = referencedQuestions.length > 0;

        if (!hasQuestions) {
            return {
                isValid: false,
                referencedQuestions,
                formulaTokens: tokens,
            };
        }

        const structureError = validateFormulaStructure(trimmedFormula);

        return {
            isValid: !structureError,
            referencedQuestions,
            formulaTokens: tokens,
            validationError: structureError,
        };
    }, [formula, questions]);
};
